import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import RichEditor from "components/RichEditor";
import Button from "components/Button";
import settingService from "api/settingsService";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

type FooterKey = "business" | "private";

interface StoredFooters {
  business: { content: string; mailboxes: number[] };
  private: { content: string; mailboxes: number[] };
}

const LS_KEY = "email_footers_v1";

const loadStored = (): StoredFooters | null => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as StoredFooters) : null;
  } catch {
    return null;
  }
};

const FOOTER_META: Record<FooterKey, { title: string }> = {
  business: { title: "Stopka biznesowa" },
  private: { title: "Stopka prywatna" },
};

const FooterConfig = () => {
  const stored = useMemo(loadStored, []);

  // Skrzynki e-mail zintegrowane z aplikacją.
  const { data: integrationsData } = settingService.useGetIntegrationSettings();
  const mailboxes = useMemo(
    () =>
      (integrationsData?.data?.Active ?? [])
        .filter((i) => i.Type === IntegrationType.Email)
        .map((i) => ({ id: i.Id, name: i.Name })),
    [integrationsData],
  );

  // Istniejąca (pojedyncza) stopka — jako domyślna treść stopki biznesowej.
  const { data: footerData } = settingService.useGetFooter();
  const { mutate: updateFooter } = settingService.useSetFooter();

  const [businessContent, setBusinessContent] = useState(
    stored?.business.content ?? "",
  );
  const [privateContent, setPrivateContent] = useState(
    stored?.private.content ?? "",
  );
  const [businessBoxes, setBusinessBoxes] = useState<number[]>(
    stored?.business.mailboxes ?? [],
  );
  const [privateBoxes, setPrivateBoxes] = useState<number[]>(
    stored?.private.mailboxes ?? [],
  );

  const businessRef = useRef<unknown>(null);
  const privateRef = useRef<unknown>(null);

  // Bez zapisanej konfiguracji — wczytaj istniejącą stopkę jako biznesową.
  useEffect(() => {
    if (!stored && footerData?.data?.Content) {
      setBusinessContent(footerData.data.Content);
    }
  }, [footerData, stored]);

  // Przypisanie skrzynek do stopki — wiele skrzynek na stopkę; skrzynka może
  // być przypisana tylko do jednej stopki (wybór w jednej zdejmuje z drugiej).
  const setAssignmentMulti = (footer: FooterKey, ids: number[]) => {
    if (footer === "business") {
      setBusinessBoxes(ids);
      setPrivateBoxes((p) => p.filter((x) => !ids.includes(x)));
    } else {
      setPrivateBoxes(ids);
      setBusinessBoxes((p) => p.filter((x) => !ids.includes(x)));
    }
  };

  const handleSave = () => {
    const payload: StoredFooters = {
      business: { content: businessContent, mailboxes: businessBoxes },
      private: { content: privateContent, mailboxes: privateBoxes },
    };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch {
      /* zapis opcjonalny */
    }
    // Zgodność wstecz: stopka biznesowa zapisywana też jako dotychczasowa stopka.
    updateFooter({ Content: businessContent });
    notification({
      title: "Konfiguracja stopki",
      text: "Stopki zostały zapisane.",
      type: ENotificationType.SUCCESS,
    });
  };

  const renderFooter = (
    key: FooterKey,
    content: string,
    setContent: (v: string) => void,
    boxes: number[],
    editorRef: React.MutableRefObject<unknown>,
  ) => (
    <div className={styles.footerCard}>
      <div className={styles.footerTitle}>{FOOTER_META[key].title}</div>

      <div className={styles.subLabel}>Skrzynki opatrywane tą stopką</div>
      <Autocomplete
        multiple
        size="small"
        options={mailboxes}
        getOptionLabel={(m) => m.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={mailboxes.filter((m) => boxes.includes(m.id))}
        onChange={(_, val) =>
          setAssignmentMulti(
            key,
            val.map((m) => m.id),
          )
        }
        noOptionsText="Brak zintegrowanych skrzynek e-mail"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Wybierz skrzynki (możesz wskazać wiele)…"
          />
        )}
      />

      <div className={styles.subLabel}>Wklej logo oraz treść stopki</div>
      <div className={styles.editor}>
        <RichEditor
          setContent={setContent}
          ref={editorRef as never}
          content={content}
          allowShowCode={true}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.intro}>
        Skonfiguruj dwie stopki wiadomości i wskaż skrzynki, w których każda z
        nich ma być używana. Pamiętaj, że jedna stopka może być używana w wielu
        skrzynkach.
      </div>

      <div className={styles.columns}>
        {renderFooter(
          "business",
          businessContent,
          setBusinessContent,
          businessBoxes,
          businessRef,
        )}
        {renderFooter(
          "private",
          privateContent,
          setPrivateContent,
          privateBoxes,
          privateRef,
        )}
      </div>

      <div className={styles.actions}>
        <Button text="Zapisz" onClick={handleSave} />
      </div>
    </div>
  );
};

export default FooterConfig;
